import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
	type ReactNode,
	type RefObject,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useProviders } from '../../../hooks/useProviders.ts'
import type { Model } from '../../../data/models.ts'
import type { GenerationCard } from '../../../types/generationCard.ts'
import type { GenerateWidgetHandle } from '../../generate/components/GenerateWidget.tsx'
import type { SunoTrack, SunoPersona } from '../../../api/music.ts'
import type { AudioOperation } from '../../../context/GenerateContext.tsx'
import { useGlobalGeneration } from '../../../context/GlobalGenerationContext.tsx'
import { attachFromStyleCard, freshCardModel } from '../../../api/attachedStyleCard.ts'
import { showToast } from '../../../components/Toast.tsx'
import {
	PENDING_GENERATION_KEY,
	isPendingExpired,
	PERSISTED_UPLOADS_KEY,
} from '../../generate/utils/constants.ts'
import { findVideoModel, DEFAULT_VIDEO_PROMPT } from '../../../hooks/useCreateVideo.ts'
import { resetScene } from '../../../api/sceneSelection'
import { pathWithoutTool } from '../utils/cardSeo.ts'
import {
	loadCollapsedState,
	resolveWidgetModel,
	saveCollapsedState,
	saveLastModel,
} from '../utils/widgetResolver.ts'
import { isTtsModelId, isTtsRoute, TTS_ROUTE } from '../../generate/utils/ttsModels'

interface BotsWidgetContextValue {
	/** Текущая модель виджета. На /bots/* (кроме slides) почти всегда не null. */
	widgetModel: Model | null
	widgetRef: RefObject<GenerateWidgetHandle | null>
	/** true = виджет свёрнут до шапки; false = раскрыт. Глобально. */
	isCollapsed: boolean
	handleSelectModel: (model: Model | null) => void
	handleStyleApply: (card: GenerationCard) => void
	handleModelChange: (model: Model | null) => void
	/** Свернуть виджет до шапки (не unmount). Сохраняется в localStorage. */
	handleCollapse: () => void
	/** Раскрыть виджет обратно. Сохраняется в localStorage. */
	handleExpand: () => void
	handleCreateVideoRequest: (imageUrl: string) => void
	/** Запустить операцию над существующим Suno-треком (extend/cover/add_vocals/add_instrumental).
	 *  Переключает виджет на audio-модель + ставит source-track в context. */
	handleTrackAction: (track: SunoTrack, operation: AudioOperation) => void
	/** Подставить голос (персону) в виджет: ставим settings.audioPersonaId и
	 *  переключаем модель на нейросеть этого голоса (Mureka-голос → Mureka-модель,
	 *  Suno → Suno), затем раскрываем виджет. */
	handleVoiceSelect: (persona: SunoPersona) => void
	/** ID голоса (персоны), который СЕЙЧАС выбран в виджете — чтобы лента
	 *  «Мои голоса» подсветила активную карточку. null = автоголос / не-audio
	 *  модель / виджет закрыт. Живо обновляется из виджета (onPersonaChange). */
	activePersonaId: string | null
	/** Колбэк для GenerateWidget — синхронизирует settings.audioPersonaId
	 *  наружу (виджет владеет своим GenerateContext, лента — снаружи). */
	handlePersonaChange: (personaId: string | null) => void
}

const BotsWidgetContext = createContext<BotsWidgetContextValue | null>(null)

interface Props {
	children: ReactNode
	onSelectModel?: (model: Model | null) => void
}

export function BotsWidgetProvider({ children, onSelectModel }: Props) {
	const { allModels } = useProviders()
	const globalGen = useGlobalGeneration()
	const location = useLocation()

	const navigate = useNavigate()
	const [widgetModel, setWidgetModel] = useState<Model | null>(null)
	const [isCollapsed, setIsCollapsed] = useState<boolean>(() => loadCollapsedState())
	// Текущий выбранный голос в виджете — для подсветки активной карточки в
	// ленте «Мои голоса». Источник истины — settings.audioPersonaId внутри
	// виджета; сюда приходит через handlePersonaChange (onPersonaChange-проп).
	const [activePersonaId, setActivePersonaId] = useState<string | null>(null)
	const widgetRef = useRef<GenerateWidgetHandle | null>(null)
	const pendingCardRef = useRef<GenerationCard | null>(null)
	// Маршрут, для которого модель уже выбрана резолвером. Нужен, чтобы СМЕНА СТАТУСА
	// генерации не перевыбирала модель заново: эффект зависит от статуса ради гидрации
	// pending-генерации, а на её завершении «последняя в разделе» затирала выбор человека.
	const resolvedForRef = useRef<string | null>(null)
	// Отложенный source-track для Suno-операций — применяется в эффекте
	// ниже, когда виджет переключится на audio-модель.
	const pendingSourceTrackRef = useRef<{ track: SunoTrack; operation: AudioOperation } | null>(null)
	// Отложенный голос (персона) — если виджет ещё не смонтирован к моменту
	// клика, применяем после монтирования (паттерн pendingSourceTrackRef).
	const pendingPersonaRef = useRef<string | null>(null)

	// Ref на onSelectModel — чтобы не перезапускать resolve-эффект
	// каждый раз, когда родитель пересоздаёт callback.
	const onSelectModelRef = useRef(onSelectModel)
	useEffect(() => {
		onSelectModelRef.current = onSelectModel
	}, [onSelectModel])

	const emitSelect = useCallback((model: Model | null) => {
		if (model) onSelectModelRef.current?.(model)
	}, [])

	// ── Resolve model на смене route / при загрузке allModels ────
	//
	// Widget на /bots/* теперь всегда смонтирован (если есть
	// подходящая модель). Этот эффект выбирает КАКУЮ модель
	// показать — коллапс/раскрытие это отдельный state ниже.
	//
	// Priority:
	//   1. Активная pending-генерация → её модель (поверх последующих)
	//   2. resolveWidgetModel(pathname): last-used / default / fallback
	useEffect(() => {
		if (allModels.length === 0) return

		// Priority 0: лендинг карточки (/…/tool/<slug>) с только что прикреплённой карточкой — её модель и есть выбор
		// человека, её ставит само применение карточки (handleStyleApply). Клик по тренду сам меняет адрес: эффект
		// лендинга (дочерний) ставил модель карточки раньше этого (родительского), и модель раздела ниже её
		// перебивала — через 2,5 с пин снимался по несовпадению модели, виджет тренда закрывался и открывался
		// обычный (владелец, 28.09). Идёт генерация — решает она (ниже).
		if (globalGen.status !== 'running' && pathWithoutTool(location.pathname) !== null && freshCardModel(allModels)) {
			resolvedForRef.current = location.pathname
			return
		}

		// Priority 1: pending generation
		try {
			const pendingSaved = localStorage.getItem(PENDING_GENERATION_KEY)
			if (pendingSaved) {
				const task = JSON.parse(pendingSaved)
				if (isPendingExpired(task)) {
					localStorage.removeItem(PENDING_GENERATION_KEY)
				} else {
					const model = allModels.find((m) => m.id === task.modelId) ?? null
					if (model) {
						// eslint-disable-next-line react-hooks/set-state-in-effect -- гидрация pending-генерации из localStorage при маунте (легитимно)
						setWidgetModel(model)
						// Активная генерация — принудительно раскрываем виджет
						setIsCollapsed(false)
						saveCollapsedState(false)
						emitSelect(model)
						return
					}
				}
			}
		} catch { /* битый localStorage — игнорируем, идём дальше */ }

		// Priority 2: resolver — только при смене маршрута (или первой загрузке моделей).
		// Завершение генерации статус меняет, а маршрут нет: до 19.09 эффект на этом
		// месте перевыбирал модель раздела (на трендах — image-модель), из-за чего
		// виджет карточки-инструмента слетал в обычный прямо в момент готовности
		// результата: чужая модель ломала рецепт карточки, и пин снимался по mismatch
		// (useDetachOnModelMismatch). Выбор человека — клик по карточке — важнее
		// «последней модели раздела».
		if (resolvedForRef.current === location.pathname) return
		resolvedForRef.current = location.pathname
		const resolution = resolveWidgetModel(location.pathname, allModels)
		if (resolution.action === 'open') {
			setWidgetModel(resolution.model)
			emitSelect(resolution.model)
		} else {
			// skip (unknown-category / no-models) — на /files, /bots/slides и т.д.
			setWidgetModel(null)
		}
	}, [location.pathname, allModels, globalGen.status, emitSelect])

	// Когда виджет открывается — применяем отложенную карточку
	useEffect(() => {
		if (!widgetModel) return
		const card = pendingCardRef.current
		if (!card) return
		pendingCardRef.current = null
		const timer = setTimeout(() => {
			widgetRef.current?.applyCard(card)
		}, 50)
		return () => clearTimeout(timer)
	}, [widgetModel])

	// Аналогично — отложенный source-track применяется после переключения
	// на audio-модель. Паттерн идентичен pendingCardRef выше.
	useEffect(() => {
		if (!widgetModel || widgetModel.category !== 'audio') return
		const pending = pendingSourceTrackRef.current
		if (!pending) return
		pendingSourceTrackRef.current = null
		const timer = setTimeout(() => {
			widgetRef.current?.applySourceTrack(pending.track, pending.operation)
		}, 50)
		return () => clearTimeout(timer)
	}, [widgetModel])

	// Отложенный голос — применяется после монтирования audio-виджета.
	useEffect(() => {
		if (!widgetModel || widgetModel.category !== 'audio') return
		const pid = pendingPersonaRef.current
		if (pid == null) return
		pendingPersonaRef.current = null
		const timer = setTimeout(() => {
			widgetRef.current?.applyPersona(pid)
		}, 50)
		return () => clearTimeout(timer)
	}, [widgetModel])

	// ── Handlers ─────────────────────────────────────────────────

	const handleSelectModel = useCallback(
		(model: Model | null) => {
			pendingCardRef.current = null
			setWidgetModel(model)
			if (model) {
				// Пользователь явно выбрал модель — раскрываем виджет
				setIsCollapsed(false)
				saveCollapsedState(false)
				saveLastModel(model.category, model.id)
				emitSelect(model)
				// Модель озвучки музыку не пишет: выбрали её в каталоге аудио —
				// уводим на «Озвучку», там виджет сам включит режим озвучки.
				if (isTtsModelId(model.id) && location.pathname.startsWith('/bots/audio') && !isTtsRoute(location.pathname)) {
					navigate(TTS_ROUTE)
				}
			}
		},
		[emitSelect, location.pathname, navigate],
	)

	const handleStyleApply = useCallback(
		(card: GenerationCard) => {
			// Clear any persisted uploads so the old image doesn't bleed into the new style
			// (смена участника мульти-тренда с keepUploads фото оставляет — снимок тоже).
			if (!card.keepUploads) localStorage.removeItem(PERSISTED_UPLOADS_KEY)

			// Пин «Выбранный стиль» для стилевых галерей /bots. Популярки
			// прикрепляют себя САМИ до этого вызова (attachFromPopularCard в
			// PopularModelsTab — там полнее данные: цена/описание/обложка);
			// их id приходит сюда с префиксом popular- — не перетираем.
			// toolMode: форс с раздела Тренды (card.toolMode) ИЛИ у стиля есть
			// наглядное демо — фокус-режим инструмента вместо сырого промпта.
			if (card.id && !card.id.startsWith('popular-'))
				attachFromStyleCard(card, {
					toolMode: card.toolMode || !!(card.demoBeforeUrl || card.demoVariants?.length),
					activeVariantKey: card.activeVariantKey,
				})

			const found = allModels.find(
				(m) => m.id === card.modelId || m.backendModel === card.modelId,
			)
			const fallbackCategory = card.isVideo ? 'video' : 'image'
			const model =
				found ??
				widgetModel ??
				allModels.find((m) => m.category === fallbackCategory) ??
				null

			if (widgetRef.current) {
				widgetRef.current.applyCard(card)
			} else {
				pendingCardRef.current = card
			}

			setWidgetModel(model)
			if (model) {
				setIsCollapsed(false)
				saveCollapsedState(false)
				saveLastModel(model.category, model.id)
				emitSelect(model)
			}
		},
		[allModels, emitSelect, widgetModel],
	)

	const handleModelChange = useCallback(
		(model: Model | null) => {
			setWidgetModel(model)
			if (model) {
				saveLastModel(model.category, model.id)
				emitSelect(model)
			}
		},
		[emitSelect],
	)

	/**
	 * Свернуть виджет до шапки (collapse). НЕ размонтирует виджет.
	 * Весь внутренний state (промпт, uploads, результат) остаётся жить.
	 */
	// Свернуть — только спрятать: карточка, загруженные фото и адрес лендинга остаются, свёрнутая полоса показывает
	// плашку карточки (владелец 29.09: «виджет, если в нём есть контент, при сворачивании самоочищается — так не
	// должно быть, юзер может потерять то, что там было»). Раньше тут срезался /tool/<slug>: смена адреса ставила модель
	// раздела, и пин снимался по несовпадению модели. Снять карточку — × инструмента (срезает адрес сам); повторный
	// клик по той же карточке раскрывает виджет (useToolCardLanding).
	const handleCollapse = useCallback(() => {
		setIsCollapsed(true)
		saveCollapsedState(true)
	}, [])

	/**
	 * Раскрыть виджет обратно (expand).
	 */
	const handleExpand = useCallback(() => {
		setIsCollapsed(false)
		saveCollapsedState(false)
	}, [])

	/**
	 * Запускает создание видео из изображения.
	 * Работает независимо от того, раскрыт виджет или свёрнут:
	 * - виджет с нужной моделью → applyCard напрямую (и раскрываем)
	 * - виджет с другой моделью → pendingCard + смена модели + раскрытие
	 */
	const handleCreateVideoRequest = useCallback(
		(imageUrl: string) => {
			const videoModel = findVideoModel(allModels)
			if (!videoModel) return

			// Снимаем персонажа: «создать видео из этого кадра» — намерение
			// оживить ГОТОВУЮ картинку, персонаж в ней уже есть. Иначе липкий
			// выбор переприменялся на видео-модели и вставал в слот вместо
			// результата, а i2v (Kling) отвечал «персонаж стал бы просто первым
			// кадром» (жалоба владельца 2026-07-25: тренд → персонаж →
			// результат → «Создать видео»). В «Моих генерациях» пути другие
			// (useCreateVideo) — там очистка добавлена тем же решением.
			resetScene()

			// Адрес держал /tool/<slug> карточки, с результата которой пришли:
			// виджет уже переключился на видео, а путь остался прежним — и
			// повторный клик по ТОЙ ЖЕ карточке ничего не делал, роутер не
			// видел смены пути (жалоба владельца 2026-07-25). Снимаем хвост,
			// контекст витрины (категория/all) остаётся.
			const stripped = pathWithoutTool(window.location.pathname)
			if (stripped) navigate(stripped, { replace: true })

			const card: GenerationCard = {
				id: 'create-video-' + Date.now(),
				type: 'generation',
				prompt: DEFAULT_VIDEO_PROMPT,
				previewUrl: imageUrl,
				sourceImageUrl: imageUrl,
				modelId: videoModel.id,
				modelName: videoModel.name,
				isVideo: false,
			}

			if (widgetRef.current && widgetModel?.id === videoModel.id) {
				// Виджет уже с нужной моделью — применяем сразу
				widgetRef.current.applyCard(card)
			} else {
				// Другая модель — ставим в очередь и переключаем
				pendingCardRef.current = card
				setWidgetModel(videoModel)
				saveLastModel(videoModel.category, videoModel.id)
				emitSelect(videoModel)
			}
			// В обоих случаях раскрываем виджет, чтобы юзер видел что происходит
			setIsCollapsed(false)
			saveCollapsedState(false)
		},
		[allModels, widgetModel, emitSelect, navigate],
	)

	/**
	 * Запускает параметрическую операцию Suno над существующим треком.
	 * Если виджет уже на audio-модели — применяем сразу. Если нет — находим
	 * audio-модель (приоритет — последняя использованная), ставим source-track
	 * в очередь через pendingSourceTrackRef и переключаем модель.
	 */
	const handleTrackAction = useCallback(
		(track: SunoTrack, operation: AudioOperation) => {
			// Дефолт модели — ПО ПРОВАЙДЕРУ ТРЕКА (Mureka-трек → Mureka-модель). Иначе
			// на Mureka-треке открывалась первая audio-модель (обычно Suno) → виджет
			// показывал чужую нейронку и её цену, хотя бэк роутит операцию по провайдеру
			// самого трека (Mureka.upload_cover/extend/…). Логика 1:1 с handleVoiceSelect.
			const providerOf = (m: Model | null) =>
				(m?.id || '').toLowerCase().startsWith('mureka') ||
				(m?.provider || '').toLowerCase() === 'mureka'
					? 'mureka'
					: 'suno'
			const trackProvider = (track.model || '').toLowerCase().startsWith('mureka') ? 'mureka' : 'suno'
			const sameModel = widgetModel?.category === 'audio' && providerOf(widgetModel) === trackProvider
			const audioModel = sameModel
				? widgetModel
				: (allModels.find((m) => m.category === 'audio' && providerOf(m) === trackProvider)
					?? allModels.find((m) => m.category === 'audio')
					?? null)
			if (!audioModel) return

			if (widgetRef.current && sameModel) {
				widgetRef.current.applySourceTrack(track, operation)
			} else {
				pendingSourceTrackRef.current = { track, operation }
				setWidgetModel(audioModel)
				saveLastModel(audioModel.category, audioModel.id)
				emitSelect(audioModel)
			}
			setIsCollapsed(false)
			saveCollapsedState(false)
		},
		[allModels, widgetModel, emitSelect],
	)

	/**
	 * Подставить голос (персону) в виджет: ставим settings.audioPersonaId и
	 * переключаем модель на нейросеть этого голоса (Mureka-голос → Mureka-модель,
	 * Suno → Suno; vocal_id ≠ personaId — провайдеры несовместимы). Текущая модель
	 * уже того же провайдера → просто выбираем голос. Иначе переключаем модель и
	 * применяем голос после монтирования audio-виджета (эффект pendingPersonaRef).
	 */
	const handleVoiceSelect = useCallback(
		(persona: SunoPersona) => {
			const providerOf = (m: Model | null) =>
				(m?.id || '').toLowerCase().startsWith('mureka') ||
				(m?.provider || '').toLowerCase() === 'mureka'
					? 'mureka'
					: 'suno'
			const voiceProvider = (persona.provider || 'suno').toLowerCase() === 'mureka' ? 'mureka' : 'suno'
			const sameModel = widgetModel?.category === 'audio' && providerOf(widgetModel) === voiceProvider

			if (sameModel) {
				if (widgetRef.current) widgetRef.current.applyPersona(persona.id)
				else pendingPersonaRef.current = persona.id
			} else {
				// Переключаем на audio-модель нужного провайдера; голос применяем
				// после монтирования (эффект pendingPersonaRef выше).
				const target = allModels.find(
					(m) => m.category === 'audio' && providerOf(m) === voiceProvider,
				)
				if (!target) {
					showToast('Нейросеть для этого голоса сейчас недоступна', 'error')
					return
				}
				pendingPersonaRef.current = persona.id
				setWidgetModel(target)
				saveLastModel(target.category, target.id)
				emitSelect(target)
			}
			setIsCollapsed(false)
			saveCollapsedState(false)
			showToast(`Голос «${persona.name || 'голос'}» подставлен в виджет`, 'success')
		},
		[allModels, widgetModel, emitSelect],
	)

	// Синхронизация выбранного голоса из виджета наружу (для active-карточки
	// в ленте). Стабильный identity — не дёргает effect виджета.
	const handlePersonaChange = useCallback((personaId: string | null) => {
		setActivePersonaId(personaId)
	}, [])

	// Active-карточка валидна только когда виджет на audio-модели. Когда
	// виджет на image/video или закрыт (widgetModel=null) — голос «не выбран»,
	// даже если в state ещё лежит прошлый id. Гейтим на ЧТЕНИИ (derive, без
	// setState в эффекте → без cascading renders).
	const exposedActivePersonaId =
		widgetModel?.category === 'audio' ? activePersonaId : null

	return (
		<BotsWidgetContext.Provider
			value={{
				widgetModel,
				widgetRef,
				isCollapsed,
				handleSelectModel,
				handleStyleApply,
				handleModelChange,
				handleCollapse,
				handleExpand,
				handleCreateVideoRequest,
				handleTrackAction,
				handleVoiceSelect,
				activePersonaId: exposedActivePersonaId,
				handlePersonaChange,
			}}
		>
			{children}
		</BotsWidgetContext.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBotsWidget() {
	const ctx = useContext(BotsWidgetContext)
	if (!ctx) throw new Error('useBotsWidget must be used inside BotsWidgetProvider')
	return ctx
}
